import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import {
  Form,
  Space,
  Button,
  Drawer,
  Upload,
  message,
  Select,
} from '@lockerpm/design';

import SelectedProjects from './SelectedProjects';
import commonComponents from '../../../../../components/common';

import {
  UploadOutlined,
} from '@ant-design/icons';

import global from '../../../../../config/global';
import storeActions from '../../../../../store/actions';

function ImportFormData(props) {
  const {  OnboardingTooltip} = commonComponents;
  const {
    visible = false,
    onClose = () => {},
  } = props
  const { t } = useTranslation();
  const selectedWorkspace = useSelector((state) => state.workspace.selectedWorkspace)
  const progressData = useSelector((state) => state.workspace.progressData)

  const [form] = Form.useForm()
  const [selectedProjects, setSelectedProjects] = useState([]);

  const format = Form.useWatch('format', form)

  useEffect(() => {
    setSelectedProjects([])
    form.setFieldsValue({
      format: global.constants.FILE_TYPE.JSON,
    })
  }, [visible])

  const uploadProps = {
    name: 'file',
    showUploadList: false,
    accept: `.${format}`,
    beforeUpload: async (file) => {
      const fileType = file.type;
      const isFormat = fileType.includes(format);

      if (!isFormat) {
        message.error(t('drag_upload.invalid'));
      } else {
        handleReadFile(file)
      }
    },
  };

  const getFileContents = async (file) => {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsText(file, 'utf-8')
      reader.onload = evt => {
        resolve(evt.target.result)
      }
      reader.onerror = () => {
        reject(Error('Error'))
      }
    })
  }

  const handleReadFile = async (file) => {
    try {
      const fileContent = await getFileContents(file);
      const projects = (JSON.parse(fileContent)?.projects || []).filter((p) => !!p.name)
      if (projects.length > 0) {
        setSelectedProjects(projects.map((p, index) => {
          const secrets =  p.secrets.filter((p) => !!p.key);
          const environments =  p.environments.filter((p) => !!p.name)
          return {
            id: index,
            name: p.name,
            description: p.description,
            secrets,
            environments,
            total_secret: secrets.length,
            total_environment: environments.length
          }
        }))
      } else {
        global.pushError({ message: t('drag_upload.content_invalid') })
      }
    } catch (error) {
      global.pushError({ message: t('drag_upload.content_invalid') })
    }
  }

  const handleClose = () => {
    form.resetFields();
    onClose()
  }

  const handleNext = () => {
    const progressId = progressData.length + 1;
    global.store.dispatch(storeActions.updateProgressData([
      ...progressData,
      {
        id: progressId,
        format: format,
        isExport: false,
        workspaceId: selectedWorkspace.id,
        createdAt: (new Date()).getTime(),
        projects: selectedProjects.map((p) => ({
          ...p,
          pSecrets: [],
          pEnvironments: [],
        }))
      }
    ]))
    handleClose();
  }

  const handleRemove = (projectId) => {
    setSelectedProjects(selectedProjects.filter((p) => p.id != projectId))
  }

  const downloadExampleFile = () => {
    const exampleContent = {
      projects: [
        {
          name: 'xxxx',
          description: 'xxxx',
          secrets: [
            {
              description: 'xxxx',
              key: "xxxx",
              value: "xxxx",
            }
          ],
          environments: [
            {
              name: "xxxx",
              description: 'xxxx',
              externalUrl: "xxxx",
            }
          ]
        }
      ]
    }
    const fileName = global.jsCore.exportService.getFileName('import-file-example', format);
    global.jsCore.platformUtilsService.saveFile(
      window,
      JSON.stringify(exampleContent, null, '  '),
      { type: 'text/plain' },
      fileName
    )
  }

  return (
    <div className={props.className}>
      <Drawer
        title={t('import_export.import')}
        placement="right"
        onClose={handleClose}
        open={visible}
        width={480}
        footer={
          <Space className='flex items-center justify-end'>
            <Button
              onClick={handleClose}
            >
              {t('button.cancel')}
            </Button>
            <Button
              type="primary"
              disabled={!selectedProjects.length}
              onClick={handleNext}
            >
              { t('button.next') } 
            </Button>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          labelAlign={'left'}
        >
          <Form.Item
            name={'format'}
            label={<div>
              <p className='font-bold'>{t('import_export.choose_format')}</p>
            </div>}
            rules={[]}
          >
            <Select
              placeholder={t('placeholder.select')}
              className="w-full"
            >
              {global.constants.IMPORT_EXPORT_FILE_TYPES.map((type) => (
                <Select.Option
                  value={type.value}
                  key={type.value}
                >
                  {type.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            className='mb-0'
            label={<div>
              <p className='font-bold'>{t('import_export.upload_file')}</p>
            </div>}
          >
            <div className='flex items-center'>
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>
                  {t('button.choose_file')}
                </Button>
              </Upload>
              <span className='ml-4 cursor-pointer text-xs' onClick={() => downloadExampleFile()}>
                <i className='mr-2 text-primary'>{t('common.file_example')}</i>
                <OnboardingTooltip
                  title={t('onboarding.file_example')}
                  placement="top"
                />
              </span>
            </div >
          </Form.Item>
          <div className='mt-4'>
            <SelectedProjects
              selectedProjects={selectedProjects}
              onRemove={(id) => handleRemove(id)}
            />
          </div>
        </Form>
      </Drawer>
    </div>
  );
}

export default ImportFormData;
